/**
 * Customize Page - Admin Settings
 * Clone of Alora Plus Customize page
 * Allows configuration of company settings, payroll, session timeout, and data import tools
 */

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../api';
import {
  Settings, Building2, Calendar, Clock, AlertTriangle, Database,
  Save, X, HelpCircle, Users, FileText, DollarSign, ClipboardList,
  Mail, Phone, Bell, Stethoscope, Tablet, Pill, RefreshCw
} from 'lucide-react';

const CustomizePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  
  // Company settings
  const [companySettings, setCompanySettings] = useState({
    companyName: 'Ivory Home Health Care, Inc.',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    fax: '',
    email: '',
    npi: '',
    taxId: ''
  });

  // Payroll settings
  const [payrollSettings, setPayrollSettings] = useState({
    firstDayOfWeek: 'Sunday',
    overtimeAlert: true,
    overtimeThreshold: 40
  });

  // Session settings
  const [sessionSettings, setSessionSettings] = useState({
    sessionTimeout: 60,
    autoLogoutWarning: true,
    warningMinutes: 5
  });

  // Data Import settings
  const [dataImportSettings, setDataImportSettings] = useState({
    enableDataImportTools: true,
    allowPatientImport: true,
    allowStaffImport: true,
    allowPhysicianImport: true
  });

  const tabs = [
    { id: 'company', label: 'Company', icon: Building2 },
    { id: 'admission', label: 'Admission', icon: FileText },
    { id: 'caregiver', label: 'Caregiver', icon: Users },
    { id: 'scheduler', label: 'Scheduler', icon: Calendar },
    { id: 'billing', label: 'Billing', icon: DollarSign },
    { id: 'assessments', label: 'Assessments', icon: ClipboardList },
    { id: 'ar', label: 'A/R', icon: DollarSign },
    { id: 'poc', label: '485/POC Plus', icon: FileText },
    { id: 'cahps', label: 'CAHPS', icon: ClipboardList },
    { id: 'pointOfCare', label: 'Point of Care', icon: Tablet },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'office', label: 'Office', icon: Building2 },
    { id: 'telephony', label: 'Telephony', icon: Phone },
    { id: 'medication', label: 'Medication Profile', icon: Pill },
    { id: 'careConnect', label: 'CareConnect', icon: Stethoscope },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings/customize');
      if (response.data) {
        if (response.data.company) setCompanySettings(response.data.company);
        if (response.data.payroll) setPayrollSettings(response.data.payroll);
        if (response.data.session) setSessionSettings(response.data.session);
        if (response.data.dataImport) setDataImportSettings(response.data.dataImport);
      }
    } catch (error) {
      // Use defaults if not found
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/customize', {
        company: companySettings,
        payroll: payrollSettings,
        session: sessionSettings,
        dataImport: dataImportSettings
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="customize-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6 text-blue-600" />
              Customize
            </h1>
            <p className="text-gray-600 mt-1">Configure system settings and preferences</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-settings-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
            <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="border-b overflow-x-auto">
            <div className="flex min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4 inline mr-1" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                <p className="mt-2 text-gray-600">Loading settings...</p>
              </div>
            ) : (
              <>
                {/* Company Tab */}
                {activeTab === 'company' && (
                  <div className="space-y-6">
                    {/* Company Information */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        Company Information
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label>Company Name</Label>
                          <Input
                            value={companySettings.companyName}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, companyName: e.target.value }))}
                            className="mt-1"
                            data-testid="company-name-input"
                          />
                        </div>
                        <div>
                          <Label>Address Line 1</Label>
                          <Input
                            value={companySettings.addressLine1}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, addressLine1: e.target.value }))}
                            className="mt-1"
                            data-testid="address-line1-input"
                          />
                        </div>
                        <div>
                          <Label>Address Line 2</Label>
                          <Input
                            value={companySettings.addressLine2}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, addressLine2: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>City</Label>
                          <Input
                            value={companySettings.city}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, city: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>State</Label>
                            <Input
                              value={companySettings.state}
                              onChange={(e) => setCompanySettings(prev => ({ ...prev, state: e.target.value }))}
                              className="mt-1"
                              maxLength={2}
                            />
                          </div>
                          <div>
                            <Label>Zip</Label>
                            <Input
                              value={companySettings.zip}
                              onChange={(e) => setCompanySettings(prev => ({ ...prev, zip: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* First Day of Week */}
                    <div className="space-y-4 border-t pt-6">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          First Day of the Week
                        </h3>
                        <button className="text-blue-600 hover:text-blue-800" title="This setting determines the start of the payroll week">
                          <HelpCircle className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {daysOfWeek.map((day) => (
                          <label key={day} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="firstDayOfWeek"
                              value={day}
                              checked={payrollSettings.firstDayOfWeek === day}
                              onChange={(e) => setPayrollSettings(prev => ({ ...prev, firstDayOfWeek: e.target.value }))}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-gray-700">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Overtime Alert */}
                    <div className="space-y-4 border-t pt-6">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        Overtime Alert
                      </h3>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="overtimeAlert"
                          checked={payrollSettings.overtimeAlert}
                          onChange={(e) => setPayrollSettings(prev => ({ ...prev, overtimeAlert: e.target.checked }))}
                          className="w-4 h-4 rounded text-blue-600"
                          data-testid="overtime-alert-checkbox"
                        />
                        <label htmlFor="overtimeAlert" className="text-gray-700">
                          Display a message when the caregiver exceeds {payrollSettings.overtimeThreshold} hours for the payroll week.
                        </label>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                        <p>
                          When enabled, the system will display a warning message when scheduling caregivers 
                          who have already worked or are scheduled to work more than {payrollSettings.overtimeThreshold} hours 
                          in the current payroll week. This helps manage overtime costs and ensure compliance 
                          with labor regulations.
                        </p>
                      </div>
                    </div>

                    {/* Session Timeout */}
                    <div className="space-y-4 border-t pt-6">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-600" />
                        Session Timeout
                      </h3>
                      <div className="flex items-center gap-4">
                        <div>
                          <Label>Session Timeout (in minutes)</Label>
                          <Input
                            type="number"
                            value={sessionSettings.sessionTimeout}
                            onChange={(e) => setSessionSettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) || 60 }))}
                            className="mt-1 w-32"
                            min={5}
                            max={480}
                            data-testid="session-timeout-input"
                          />
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                        <p>
                          The session timeout determines how long a user can remain inactive before being 
                          automatically logged out. A shorter timeout enhances security but may require 
                          more frequent logins. The default value is 60 minutes.
                        </p>
                      </div>
                    </div>

                    {/* Data Import Tools */}
                    <div className="space-y-4 border-t pt-6">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Database className="w-5 h-5 text-green-600" />
                        Data Import Tools
                      </h3>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="enableDataImport"
                          checked={dataImportSettings.enableDataImportTools}
                          onChange={(e) => setDataImportSettings(prev => ({ ...prev, enableDataImportTools: e.target.checked }))}
                          className="w-4 h-4 rounded text-blue-600"
                          data-testid="enable-data-import-checkbox"
                        />
                        <label htmlFor="enableDataImport" className="text-gray-700">
                          Enable Data Import Tools
                        </label>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
                        <p>
                          The Data Import Tools are designed to help agencies set up their system quickly 
                          by importing existing data from spreadsheets. This feature is particularly useful 
                          during initial setup and onboarding periods. When enabled, administrators can 
                          import patient, staff, and physician data from CSV files.
                        </p>
                      </div>
                      {dataImportSettings.enableDataImportTools && (
                        <div className="ml-6 space-y-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={dataImportSettings.allowPatientImport}
                              onChange={(e) => setDataImportSettings(prev => ({ ...prev, allowPatientImport: e.target.checked }))}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-gray-600">Allow Patient Import</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={dataImportSettings.allowStaffImport}
                              onChange={(e) => setDataImportSettings(prev => ({ ...prev, allowStaffImport: e.target.checked }))}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-gray-600">Allow Staff Import</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={dataImportSettings.allowPhysicianImport}
                              onChange={(e) => setDataImportSettings(prev => ({ ...prev, allowPhysicianImport: e.target.checked }))}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-gray-600">Allow Physician Import</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Other Tabs - Coming Soon */}
                {activeTab !== 'company' && (
                  <div className="text-center py-12">
                    <Settings className="w-12 h-12 mx-auto text-gray-300" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">
                      {tabs.find(t => t.id === activeTab)?.label} Settings
                    </h3>
                    <p className="mt-2 text-gray-500">
                      Configuration options for this section are coming soon.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomizePage;
